import { Component, effect, input, output, untracked, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PaneComponent, YesNoIconComponent } from '@rocksoft/cdk';
import { ModelNameFlags } from '@rocksoft/locations-sdk';
import { CategoryProfileDto } from '@rocksoft/settings-sdk';

export interface ProfilePresetOption {
  id: string;
  displayName: string;
  displayNameString?: string;
}

export type ConfigurationSetPerModelName = Record<Exclude<ModelNameFlags, 'None' | 'All'>, string[]>;
export interface MatrixOptions {
  settingsTableHeader: string;
  settingsFirstColumnHeader: string;
  settingsSecondColumnHeader?: string;
  allowMultiple: boolean;
  displayedModels: ModelNameFlags[];
}

type MatrixModel = Exclude<ModelNameFlags, 'None' | 'All'>;

const MATRIX_MODELS: MatrixModel[] = ['iPad', 'iPod', 'iPhone', 'AppleTV'];
const MODEL_FLAGS: Record<MatrixModel, ModelNameFlags> = {
  iPad: ModelNameFlags.IPad,
  iPod: ModelNameFlags.IPod,
  iPhone: ModelNameFlags.IPhone,
  AppleTV: ModelNameFlags.AppleTv,
};

@Component({
  selector: 'era-selection-matrix',
  templateUrl: './selection-matrix.component.html',
  styleUrls: ['./selection-matrix.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PaneComponent, ReactiveFormsModule, FaIconComponent, YesNoIconComponent],
})
export class SelectionMatrixComponent {
  public localProfiles: CategoryProfileDto[] = [];

  public readonly matrixOptions = input.required<MatrixOptions>();
  public readonly allowEdit = input(true);
  public readonly availableProfiles = input<ProfilePresetOption[] | null>([]);
  public readonly resolvedProfiles = input<CategoryProfileDto[] | null>([]);
  public readonly settingSelectionChange = output<CategoryProfileDto[]>();

  public editing = false;
  public modelsSetPerSettingForm: UntypedFormGroup = new UntypedFormGroup({});

  constructor() {
    effect(() => {
      const options = this.matrixOptions();
      if (this.localProfiles.length > 0) {
        // Recreate the controls when the options change. Needed because of allowMultiple.
        this.matrixToFormGroup(options);
      }
    });

    effect(() => {
      const profiles = this.resolvedProfiles() ?? [];
      const options = untracked(() => this.matrixOptions());

      this.resetConfigurationState(profiles);
      this.matrixToFormGroup(options);
    });
  }

  public profileDisplayName(profile: CategoryProfileDto): string {
    return (
      this.availableProfiles()?.find((availableProfile) => availableProfile.id === profile.id)?.displayName ??
      profile.displayName
    );
  }

  public profileDisplayNameString(profile: CategoryProfileDto): string | undefined {
    return this.availableProfiles()?.find((availableProfile) => availableProfile.id === profile.id)?.displayNameString;
  }

  public isProfileDisplayed(profileId: string): boolean {
    return this.localProfiles.some((profile) => profile.id === profileId);
  }

  private matrixToFormGroup(options: MatrixOptions) {
    this.modelsSetPerSettingForm.setControl('setting', new UntypedFormControl());

    if (options.allowMultiple) {
      for (const profile of this.localProfiles) {
        for (const model of MATRIX_MODELS) {
          this.modelsSetPerSettingForm.setControl(
            `${profile.id}_${model}`,
            new UntypedFormControl(this.profileHasModel(profile, model))
          );
        }
      }
    } else {
      for (const model of MATRIX_MODELS) {
        const selectedProfile = this.localProfiles.find((profile) => this.profileHasModel(profile, model));
        this.modelsSetPerSettingForm.setControl(model, new UntypedFormControl(selectedProfile?.id));
      }
    }
  }

  public edit() {
    this.editing = true;
  }

  public cancel() {
    this.editing = false;
    this.resetConfigurationState(this.resolvedProfiles() ?? []);
    this.matrixToFormGroup(this.matrixOptions());
  }

  public save() {
    this.editing = false;
    const savedProfiles = this.matrixOptions().allowMultiple
      ? this.buildSavedProfilesForMultiple()
      : this.buildSavedProfilesForSingle();

    this.settingSelectionChange.emit(savedProfiles);

    // Makes sure inherited configurations are shown again after they have been removed from the
    // matrix and no new configuration is added.
    this.resetConfigurationState(this.resolvedProfiles() ?? []);
    this.matrixToFormGroup(this.matrixOptions());
  }

  public add() {
    const selectedProfile = this.modelsSetPerSettingForm.get('setting')?.value as ProfilePresetOption | undefined;
    if (!selectedProfile || this.localProfiles.some((profile) => profile.id === selectedProfile.id)) return;

    this.localProfiles = [
      ...this.localProfiles,
      {
        id: selectedProfile.id,
        displayName: selectedProfile.displayName,
        models: [],
      },
    ];
    this.matrixToFormGroup(this.matrixOptions());
  }

  public delete(profile: CategoryProfileDto) {
    this.localProfiles = this.localProfiles.filter((localProfile) => localProfile.id !== profile.id);
    this.matrixToFormGroup(this.matrixOptions());
  }

  private resetConfigurationState(profiles: CategoryProfileDto[]) {
    // The resolved store already contains the effective profile for the current category. Keep all
    // rows and let the matrix only handle editing the model selections.
    this.localProfiles = profiles.map((profile) => ({ ...profile, models: [...(profile.models ?? [])] }));
  }

  private buildSavedProfilesForMultiple(): CategoryProfileDto[] {
    const resolvedProfiles = this.resolvedProfiles() ?? [];
    const resolvedById = new Map(resolvedProfiles.map((profile) => [profile.id, profile]));
    const savedProfiles: CategoryProfileDto[] = [];

    for (const profile of this.localProfiles) {
      const selectedModels = this.selectedModelsForProfile(profile.id, true);
      const originalProfile = resolvedById.get(profile.id);
      const hasChanges = this.hasModelSelectionChanged(selectedModels, originalProfile);
      const isDirectConfiguration = originalProfile !== undefined && originalProfile.inheritedFrom == null;

      if (hasChanges || isDirectConfiguration) {
        savedProfiles.push({ ...profile, models: selectedModels });
      }
    }

    return savedProfiles;
  }

  private buildSavedProfilesForSingle(): CategoryProfileDto[] {
    const resolvedProfiles = this.resolvedProfiles() ?? [];
    const resolvedById = new Map(resolvedProfiles.map((profile) => [profile.id, profile]));
    const selectedProfileIds = new Set(
      MATRIX_MODELS.map((model) => this.modelsSetPerSettingForm.get(model)?.value).filter(
        (profileId): profileId is string => !!profileId
      )
    );
    const savedProfiles: CategoryProfileDto[] = [];

    for (const profile of this.localProfiles) {
      if (!selectedProfileIds.has(profile.id)) continue;

      const selectedModels = this.selectedModelsForProfile(profile.id, false);
      const originalProfile = resolvedById.get(profile.id);
      const hasChanges = this.hasModelSelectionChanged(selectedModels, originalProfile);
      const isDirectConfiguration = originalProfile !== undefined && originalProfile.inheritedFrom == null;

      if (hasChanges || isDirectConfiguration) {
        savedProfiles.push({ ...profile, models: selectedModels });
      }
    }

    return savedProfiles;
  }

  private selectedModelsForProfile(profileId: string, multiple: boolean): ModelNameFlags[] {
    return MATRIX_MODELS.filter((model) => {
      const controlName = multiple ? `${profileId}_${model}` : model;
      const value = this.modelsSetPerSettingForm.get(controlName)?.value;
      return value === true || value === profileId;
    }).map((model) => MODEL_FLAGS[model]);
  }

  private hasModelSelectionChanged(selectedModels: ModelNameFlags[], originalProfile?: CategoryProfileDto): boolean {
    if (!originalProfile) return selectedModels.length > 0;

    return MATRIX_MODELS.some((model) => {
      const selected = selectedModels.includes(MODEL_FLAGS[model]);
      return selected !== this.profileHasModel(originalProfile, model);
    });
  }

  private profileHasModel(profile: CategoryProfileDto, model: MatrixModel): boolean {
    return profile.models?.includes(MODEL_FLAGS[model]) || profile.models?.includes(ModelNameFlags.All) || false;
  }
}
